import { useState } from 'react'
import { Link } from 'react-router-dom'
import Reveal from '../components/landing/Reveal'
import { getTrackingContext } from '../services/websiteTracker'
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  Instagram,
  Facebook,
  Youtube,
  ArrowLeft
} from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState({ type: '', message: '' })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus({ type: '', message: '' })
    setIsSubmitting(true)
    try {
      const tracking = getTrackingContext()
      const response = await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          website: formData.website,
          visitorId: tracking.visitorId,
          sessionId: tracking.sessionId,
          utmSource: tracking.utmSource,
          utmMedium: tracking.utmMedium,
          utmCampaign: tracking.utmCampaign,
          utmTerm: tracking.utmTerm,
          utmContent: tracking.utmContent,
          landingPage: typeof window !== 'undefined' ? window.location.pathname : '/contact',
          referrer: typeof document !== 'undefined' ? document.referrer : '',
        }),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok || payload.ok === false) {
        setStatus({
          type: 'error',
          message: payload.error || 'We could not send your message just now. Please try again.',
        })
        return
      }
      setStatus({
        type: 'success',
        message: payload.message || 'Thank you for contacting us. Our admissions team will get back to you shortly.',
      })
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', website: '' })
    } catch {
      setStatus({
        type: 'error',
        message: 'We could not send your message just now. Please try again, or call SMA Admissions.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <div className="cinema-grain" aria-hidden="true" />
      {/* Minimalist Header */}
      <header className="cinema-glass-strong sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <Link to="/explore" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="group">
              <img
                src="/Logos/SMA LOGO  DARK MODE.png"
                alt="Synergy Multimedia Academy logo"
                className="h-10 opacity-90 transition-transform duration-500 group-hover:scale-105"
              />
            </Link>
            <div className="flex items-center gap-6 sm:gap-8">
              <Link to="/" className="inline-flex items-center gap-2 text-cinema-soft hover:text-cinema-gold text-xs uppercase tracking-[0.2em] font-sans font-medium transition-colors group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <Link to="/register" className="cinema-btn-primary !px-6 !py-3 !min-h-[44px] !text-xs">
                Enroll
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Cinematic */}
      <section className="relative py-20 sm:py-28 lg:py-36 overflow-hidden">
        <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 -right-40 opacity-50" />
        <div className="cinema-glow-orb cinema-glow-orb-blue w-[400px] h-[400px] bottom-0 -left-20" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <Reveal>
            <span className="cinema-label">Get in Touch</span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="cinema-headline-xl mt-6 mb-6">
              Let's <span className="text-transparent bg-clip-text bg-gradient-to-r from-cinema-gold via-cinema-glow to-cinema-amber">Talk</span>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="cinema-body-lg max-w-2xl">
              Have questions about our programs? We're here to help you start your creative journey.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Main Content - 2 Column Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-16 sm:pb-24 lg:pb-32">
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-0 items-start">
          
          {/* Left Column - Contact Information */}
          <Reveal
            as="div"
            itemScope 
            itemType="http://schema.org/LocalBusiness"
            className="space-y-8 sm:space-y-10 lg:space-y-12 lg:pr-20"
          >
            <meta itemProp="name" content="Synergy Multimedia Academy" />
            <meta itemProp="image" content="https://synergymultimediaacademy.com/Logos/SMA%20LOGO%20%20DARK%20MODE.png" />
            <meta itemProp="priceRange" content="400000-4500000 UGX" />
            
            {/* Address */}
            <div className="group">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-cinema-gold flex-shrink-0 mt-1.5 group-hover:text-cinema-glow transition-colors" strokeWidth={1.5} />
                <div>
                  <h3 
                    className="cinema-label mb-3"
                  >
                    Location
                  </h3>
                  <div itemProp="address" itemScope itemType="http://schema.org/PostalAddress">
                    <p 
                      className="text-base sm:text-lg text-white leading-relaxed"
                      style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                    >
                      <span itemProp="streetAddress">Makerere I - Kubiri</span><br />
                      <span itemProp="addressLocality">Kampala</span>,{' '}
                      <span itemProp="addressCountry">Uganda</span>
                    </p>
                  </div>
                  <meta itemProp="latitude" content="0.345352" />
                  <meta itemProp="longitude" content="32.571540" />
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="group">
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-cinema-gold flex-shrink-0 mt-1.5 group-hover:text-cinema-glow transition-colors" strokeWidth={1.5} />
                <div>
                  <h3 
                    className="cinema-label mb-3"
                  >
                    Phone
                  </h3>
                  <a 
                    href="tel:+256706739311"
                    itemProp="telephone"
                    className="text-base sm:text-lg text-white hover:text-cinema-gold transition-colors"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                  >
                    +256 706 739 311
                  </a>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="group">
              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-cinema-gold flex-shrink-0 mt-1.5 group-hover:text-cinema-glow transition-colors" strokeWidth={1.5} />
                <div>
                  <h3 
                    className="cinema-label mb-3"
                  >
                    Email
                  </h3>
                  <a 
                    href="mailto:synergymultimediaacademy@gmail.com"
                    itemProp="email"
                    className="text-base sm:text-lg text-white hover:text-cinema-gold transition-colors"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                  >
                    synergymultimediaacademy@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="group">
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-cinema-gold flex-shrink-0 mt-1.5 group-hover:text-cinema-glow transition-colors" strokeWidth={1.5} />
                <div>
                  <h3 
                    className="cinema-label mb-3"
                  >
                    Office Hours
                  </h3>
                  <div 
                    className="text-base sm:text-lg text-white space-y-1"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                  >
                    <p>Mon-Fri<span className="text-cinema-muted ml-3">9am - 6pm</span></p>
                    <p>Sat<span className="text-cinema-muted ml-3">11am - 3pm</span></p>
                  </div>
                  <meta itemProp="openingHours" content="Mo-Fr 09:00-18:00" />
                  <meta itemProp="openingHours" content="Sa 11:00-15:00" />
                </div>
              </div>
            </div>

            {/* Social Media - Minimal Monochromatic Icons */}
            <div className="pt-8 border-t border-white/10">
              <h3 className="cinema-label mb-6">
                Follow Us
              </h3>
              <div className="flex gap-6">
                <a
                  href="https://www.instagram.com/synergymultimediaacademy?igsh=MXRrNDBiMDBtbTE4NA=="
                  target="_blank"
                  rel="noopener noreferrer"
                  itemProp="sameAs"
                  className="text-cinema-muted hover:text-cinema-gold transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" strokeWidth={1.5} />
                </a>
                <a
                  href="https://www.facebook.com/synergymultimediaacademy"
                  target="_blank"
                  rel="noopener noreferrer"
                  itemProp="sameAs"
                  className="text-cinema-muted hover:text-cinema-gold transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" strokeWidth={1.5} />
                </a>
                <a
                  href="https://www.youtube.com/synergymultimediaacademy"
                  target="_blank"
                  rel="noopener noreferrer"
                  itemProp="sameAs"
                  className="text-cinema-muted hover:text-cinema-gold transition-colors"
                  aria-label="YouTube"
                >
                  <Youtube className="w-5 h-5" strokeWidth={1.5} />
                </a>
                <a
                  href="https://www.tiktok.com/@synergy.multimedi?_r=1&_t=ZS-94AjYChnMCj"
                  target="_blank"
                  rel="noopener noreferrer"
                  itemProp="sameAs"
                  className="text-cinema-muted hover:text-cinema-gold transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
          </Reveal>

          {/* Vertical Divider */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />

          {/* Right Column - Contact Form */}
          <Reveal as="div" delay={150} className="lg:pl-20">
            <div className="cinema-glass rounded-sm p-8 sm:p-10 lg:p-12">
              <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              <div className="hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>

              {status.message ? (
                <p
                  role="status"
                  className={`text-sm font-sans ${status.type === 'error' ? 'text-red-400' : 'text-cinema-gold'}`}
                >
                  {status.message}
                </p>
              ) : null}
              {/* Name Input - Ghost Style */}
              <div className="group">
                <label 
                  htmlFor="name" 
                  className="cinema-label block mb-4"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-0 border-b border-white/10 text-white text-base sm:text-lg pb-3 focus:outline-none focus:border-cinema-gold/50 transition-colors placeholder-cinema-muted"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                  placeholder="Your full name"
                />
              </div>

              {/* Email Input - Ghost Style */}
              <div className="group">
                <label 
                  htmlFor="email" 
                  className="cinema-label block mb-4"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-0 border-b border-white/10 text-white text-base sm:text-lg pb-3 focus:outline-none focus:border-cinema-gold/50 transition-colors placeholder-cinema-muted"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                  placeholder="you@example.com"
                />
              </div>

              {/* Phone Input - Ghost Style */}
              <div className="group">
                <label 
                  htmlFor="phone" 
                  className="cinema-label block mb-4"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-white/10 text-white text-base sm:text-lg pb-3 focus:outline-none focus:border-cinema-gold/50 transition-colors placeholder-cinema-muted"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                  placeholder="+256 700 000 000"
                />
              </div>

              {/* Subject Input - Ghost Style */}
              <div className="group">
                <label 
                  htmlFor="subject" 
                  className="cinema-label block mb-4"
                >
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-0 border-b border-white/10 text-white text-base sm:text-lg pb-3 focus:outline-none focus:border-cinema-gold/50 transition-colors placeholder-cinema-muted"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                  placeholder="What's this about?"
                />
              </div>

              {/* Message Textarea - Ghost Style */}
              <div className="group">
                <label 
                  htmlFor="message" 
                  className="cinema-label block mb-4"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full bg-transparent border-0 border-b border-white/10 text-white text-base sm:text-lg pb-3 focus:outline-none focus:border-cinema-gold/50 transition-colors placeholder-cinema-muted resize-none"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 400 }}
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>

              {/* Submit Button - Cinematic */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`group ${
                  isSubmitting
                    ? 'inline-flex items-center justify-center gap-2.5 px-8 py-4 min-h-[52px] bg-cinema-slate text-cinema-muted cursor-not-allowed font-display font-medium text-sm tracking-wide w-full md:w-auto'
                    : 'cinema-btn-primary w-full md:w-auto'
                }`}
              >
                {isSubmitting ? (
                  <>Sending...</>
                ) : (
                  <>
                    Send Message
                    <Send className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
            </div>
          </Reveal>

        </div>
      </section>

      {/* Full-Bleed Map */}
      <section className="relative w-full h-[350px] sm:h-[450px] lg:h-[600px] border-t border-white/5 grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-700">
        <iframe
          title="Synergy Multimedia Academy Location - Makerere I Kubiri, Kampala Uganda - Photography & Multimedia Academy"
          src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3989.757890123456!2d32.571540!3d0.345352!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMMKwMjAnNDMuMyJOIDMywrAzNCcxNy41IkU!5e0!3m2!1sen!2sug!4v1234567890123!5m2!1sen!2sug"
          width="100%"
          height="100%"
          style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) saturate(0.3)' }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>

      {/* Footer */}
      <footer className="bg-cinema-black border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-cinema-muted text-sm font-sans">
            &copy; {new Date().getFullYear()} Synergy Multimedia Academy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}

export default ContactPage
